<template>
  <div id="anchorlinkmenu" class="sticky z-[24] w-full transition-[top] duration-300 ease-[cubic-bezier(0.43,0.05,0.17,1)] will-change-[top]" :style="topStyle">
    <div class="relative">
      <div ref="menuContainerRef" class="scrollbar-hide flex h-12 w-full items-stretch overflow-x-auto border-b border-t border-gray1 bg-white shadow-inner md:justify-around md:overflow-visible">
        <template v-for="(menuText, index) in menuTexts" :key="index">
          <div
            :ref="(el) => setTabRef(el as HTMLElement | null, index)"
            class="relative flex h-full shrink-0 cursor-pointer items-center justify-center whitespace-nowrap px-2 md:flex-1 md:shrink md:px-0"
            @click="onClickSectionMenuButton(index)"
          >
            <span :class="[buttonStyle, isActiveTab(index) ? 'text-gold3' : 'text-gray2 md:hover:text-gold3']">{{ menuText }}</span>
            <div class="absolute bottom-0 w-full bg-gold3" :class="{ 'h-[4px]': isActiveTab(index) }"></div>
          </div>
        </template>
        <!-- モバイル: 右端グラデーション分のスペーサー -->
        <div class="h-full w-8 shrink-0 md:hidden"></div>
      </div>
      <!-- モバイル: 右端グラデーションシャドウ（スクロールコンテナの外側に固定） -->
      <div class="pointer-events-none absolute right-0 top-0 h-10 w-8 md:hidden" style="background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 50%)"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useElementBounding } from '@vueuse/core';
import { debounce, floor } from 'lodash';

import { useIsSP } from '@/composables/ui/useIsSP';
import { useSafeWindow } from '@/composables/utils/useSafeWindow';

interface Props {
  menuTexts: string[];
  targetRefs: Ref<HTMLElement | null>[];
  scrollOffset?: number;
  isScrollingUp?: boolean;
  /** ヘッダー要素のセレクタ（動的にtop位置を計算するため） */
  headerSelector?: string;
  /** 切り替え系タブのインデックス配列（例: [1, 2] で The Story と Gallery） */
  switchTabIndices?: number[];
  /** 現在アクティブな切り替えタブのインデックス */
  activeSwitchTabIndex?: number;
  /** 切り替え系タブがクリックされた時のハンドラ */
  onSwitchTab?: (index: number) => void;
}

const props = withDefaults(defineProps<Props>(), {
  scrollOffset: 0,
  isScrollingUp: false,
  headerSelector: '#js-header',
  switchTabIndices: () => [],
  activeSwitchTabIndex: -1,
  onSwitchTab: undefined,
});

// 定数
const SCROLL_POSITION_THRESHOLD = 2; // スクロール到達判定の許容誤差(px)
const SCROLL_TIMEOUT_MS = 2000; // スクロール完了待機のタイムアウト(ms)
const HEADER_OBSERVER_RETRY_INTERVAL_MS = 100; // ヘッダー検索リトライ間隔
const HEADER_OBSERVER_RETRY_TIMEOUT_MS = 1000; // ヘッダー検索リトライ最大時間

const { window } = useSafeWindow();
const isSP = useIsSP();

// タブバーのref（モバイルでのアクティブタブセンタリング用）
const menuContainerRef = ref<HTMLElement | null>(null);
const tabRefs = ref<(HTMLElement | null)[]>([]);
const setTabRef = (el: HTMLElement | null, index: number) => {
  tabRefs.value[index] = el;
};

// モバイル: アクティブタブを中央にスクロール
const centerActiveTab = (index: number) => {
  if (!isSP.value || !menuContainerRef.value || index < 0) return;
  const tab = tabRefs.value[index];
  if (!tab) return;
  const container = menuContainerRef.value;
  const scrollLeft = tab.offsetLeft - container.offsetWidth / 2 + tab.offsetWidth / 2;
  container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
};

// ページスクロールによるタブ変更時のセンタリング（デバウンス付き）
const debouncedCenterActiveTab = debounce(centerActiveTab, 150);

// ヘッダー高さ（動的に計測、スクロールオフセット計算用）
const headerHeight = ref(0);
let resizeObserver: ResizeObserver | null = null;

// 各セクションのboundingを取得
const targetBoundings = props.targetRefs.map((targetRef) => useElementBounding(targetRef));
const targetYs = targetBoundings.map((bounding) => bounding.y);

// 最後のセクションのbottomを取得（全セクションを過ぎたら非選択にするため）
const lastTargetBounding = computed(() => targetBoundings[targetBoundings.length - 1]);

// スクロールオフセット計算（上スクロール時はヘッダー高さを考慮）
const totalScrollOffset = computed(() => props.scrollOffset + (props.isScrollingUp ? -headerHeight.value : 0));

// Y座標が0以下の最後のインデックスを取得
const findLastIndexBelowZero = (yOffsetsWithOffset: number[]) => {
  return yOffsetsWithOffset.reduce((accumulatedIndex, currentOffsetY, currentIndex) => {
    return currentOffsetY <= 0 ? currentIndex : accumulatedIndex;
  }, -1);
};

// スクロール位置に基づくアクティブタブのインデックス
const selectedMenuIndex = ref<number>(-1);

// スムーススクロール中のタブ自動切り替えを抑制するフラグ
const isScrolling = ref(false);
// クリックで選択されたタブのインデックス（スムーススクロール中に使用）
const manuallySelectedIndex = ref<number>(-1);
// スクロール到達チェック用
let targetScrollY: number | null = null;
let scrollCheckRAF: number | null = null;
let scrollStartTime: number | null = null;
// ヘッダー検索リトライ用タイマー
let headerRetryInterval: ReturnType<typeof setInterval> | null = null;
let headerRetryTimeout: ReturnType<typeof setTimeout> | null = null;

const onScrollEnd = () => {
  // スクロール完了時、先にselectedMenuIndexを更新してからフラグをクリア
  // 最後のセクションを完全に通過したら非選択
  const lastSectionBottom = lastTargetBounding.value.bottom.value + totalScrollOffset.value;
  if (lastSectionBottom <= 0) {
    selectedMenuIndex.value = -1;
  } else {
    const targetYsWithOffset = targetYs.map((targetY) => floor(targetY.value + totalScrollOffset.value));
    selectedMenuIndex.value = findLastIndexBelowZero(targetYsWithOffset);
  }
  // フラグをクリア
  isScrolling.value = false;
  manuallySelectedIndex.value = -1;
  targetScrollY = null;
  scrollStartTime = null;
  if (scrollCheckRAF) {
    cancelAnimationFrame(scrollCheckRAF);
    scrollCheckRAF = null;
  }
};

// スクロール位置が目標に到達したかチェック（タイムアウト付き）
const checkScrollPosition = () => {
  if (!window.value || targetScrollY === null) return;

  // タイムアウトチェック（ユーザー介入でスクロールが中断された場合の安全装置）
  if (scrollStartTime && Date.now() - scrollStartTime > SCROLL_TIMEOUT_MS) {
    onScrollEnd();
    return;
  }

  const currentY = window.value.scrollY;
  if (Math.abs(currentY - targetScrollY) < SCROLL_POSITION_THRESHOLD) {
    // 目標位置に到達
    onScrollEnd();
  } else {
    // まだ到達していない場合は再チェック
    scrollCheckRAF = requestAnimationFrame(checkScrollPosition);
  }
};

const updateHeaderHeight = () => {
  const header = document.querySelector(props.headerSelector);
  if (header) {
    const height = header.getBoundingClientRect().height;
    if (height > 0) {
      headerHeight.value = height;
    }
  }
};

const setupHeaderObserver = () => {
  const header = document.querySelector(props.headerSelector);
  if (header) {
    updateHeaderHeight();
    resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });
    resizeObserver.observe(header);
    return true;
  }
  return false;
};

onMounted(async () => {
  // ヘッダーが<client-only>内にあるため、nextTickで待機
  await nextTick();
  if (!setupHeaderObserver()) {
    // ヘッダーがまだ見つからない場合、少し待ってリトライ
    headerRetryInterval = setInterval(() => {
      if (setupHeaderObserver()) {
        if (headerRetryInterval) clearInterval(headerRetryInterval);
        if (headerRetryTimeout) clearTimeout(headerRetryTimeout);
        headerRetryInterval = null;
        headerRetryTimeout = null;
      }
    }, HEADER_OBSERVER_RETRY_INTERVAL_MS);
    headerRetryTimeout = setTimeout(() => {
      if (headerRetryInterval) clearInterval(headerRetryInterval);
      headerRetryInterval = null;
      headerRetryTimeout = null;
    }, HEADER_OBSERVER_RETRY_TIMEOUT_MS);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (scrollCheckRAF) {
    cancelAnimationFrame(scrollCheckRAF);
  }
  // ヘッダー検索リトライのクリーンアップ
  if (headerRetryInterval) {
    clearInterval(headerRetryInterval);
  }
  if (headerRetryTimeout) {
    clearTimeout(headerRetryTimeout);
  }
});

// 動的なtop位置（上スクロール時: ヘッダーの下、下スクロール時: 最上部）
const topStyle = computed(() => ({
  top: props.isScrollingUp ? `${headerHeight.value}px` : '0px',
}));

const isSwitchTab = (index: number) => props.switchTabIndices.includes(index);

// クリック時にターゲット位置を判定してスクロール（目標位置を返す）
const scrollToTarget = (index: number): number | null => {
  if (!props.targetRefs[index]?.value || !window.value) return null;

  const targetY = targetYs[index].value;
  // 上方向スクロール時はヘッダー表示分のオフセットを追加
  const additionalOffset = targetY < 0 ? -headerHeight.value : 0;
  const destination = window.value.scrollY + targetY + props.scrollOffset + additionalOffset;

  window.value.scrollTo({
    top: destination,
    behavior: 'smooth',
  });

  return destination;
};

const onClickSectionMenuButton = (index: number) => {
  // スクロール中は自動更新をオフにし、クリックしたタブを手動選択として記録
  isScrolling.value = true;
  manuallySelectedIndex.value = index;

  // モバイル: クリックしたタブを中央にスクロール
  centerActiveTab(index);

  if (isSwitchTab(index)) {
    // 切り替え系タブの場合は親に通知
    props.onSwitchTab?.(index);
  }

  // スクロール実行と位置チェック開始
  const destination = scrollToTarget(index);
  if (destination !== null) {
    targetScrollY = destination;
    scrollStartTime = Date.now();
    // 位置チェック開始
    if (scrollCheckRAF) cancelAnimationFrame(scrollCheckRAF);
    scrollCheckRAF = requestAnimationFrame(checkScrollPosition);
  }
};

// スクロール位置に基づくアクティブタブの自動更新
watchEffect(() => {
  if (isScrolling.value) return;
  // 最後のセクションを完全に通過したら非選択
  const lastSectionBottom = lastTargetBounding.value.bottom.value + totalScrollOffset.value;
  if (lastSectionBottom <= 0) {
    selectedMenuIndex.value = -1;
    return;
  }
  const targetYsWithOffset = targetYs.map((targetY) => floor(targetY.value + totalScrollOffset.value));
  selectedMenuIndex.value = findLastIndexBelowZero(targetYsWithOffset);
});

// 現在のアクティブタブのインデックス（スクロール中は手動選択を優先）
const effectiveIndex = computed(() => (isScrolling.value && manuallySelectedIndex.value >= 0 ? manuallySelectedIndex.value : selectedMenuIndex.value));

// ページスクロールによるアクティブタブ変更時、タブを中央にスクロール（デバウンス付き）
watch(effectiveIndex, (newIndex) => {
  // タブクリック時はonClickSectionMenuButtonで即座にセンタリングするのでスキップ
  if (isScrolling.value) return;
  debouncedCenterActiveTab(newIndex);
});

/** タブがアクティブかどうか判定 */
const isActiveTab = (index: number) => {
  // 切り替え系タブの場合
  if (isSwitchTab(index)) {
    // スクロール位置が切り替え系タブのいずれかに該当し、かつ現在アクティブな切り替えタブと一致する場合
    return props.switchTabIndices.includes(effectiveIndex.value) && index === props.activeSwitchTabIndex;
  }
  // 通常タブの場合
  return index === effectiveIndex.value;
};

const buttonStyle = 'block flex-1 text-center font-Spectral text-[16px] font-normal leading-6';
</script>

<style scoped>
/* スクロールバー非表示 */
.scrollbar-hide {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
